import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUtmTaxonomyStore, type Category } from '@/store/utm-taxonomy-store';
import UtmDataTable from '@/components/utm/shared/UtmDataTable';
import { Plus, Edit, Trash2 } from 'lucide-react';
import { Icon, type IconName } from '@/components/utm/shared/UtmIconPicker';
import { resolveUtmIconUrl } from '@/components/utm/shared/utm-icon-url';

const CategoryIconCell = ({ category }: { category: Category }) => {
  const iconUrl = category.iconType === 'S3_OBJECT' ? resolveUtmIconUrl(category.iconValue) : null;

  return (
    <div className="flex items-center">
      {category.iconType === 'BUILTIN' ? (
        <div
          className="w-10 h-10 rounded-lg flex items-center justify-center"
          style={{ backgroundColor: category.iconBgColor || '#1e293b' }}
        >
          <Icon
            name={category.iconValue as IconName}
            size={20}
            style={{ color: category.iconColor || '#fff' }}
          />
        </div>
      ) : category.iconType === 'S3_OBJECT' && iconUrl ? (
        <div
          className="w-10 h-10 rounded-lg flex items-center justify-center overflow-hidden"
          style={{ backgroundColor: category.iconBgColor || '#1e293b' }}
        >
          <img
            src={iconUrl}
            alt={category.name}
            className="w-8 h-8 object-contain"
          />
        </div>
      ) : (
        <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center" />
      )}
    </div>
  );
};

const UtmCategoryList = () => {
  const navigate = useNavigate();
  const { categories, groups, deleteCategory, isLoading } = useUtmTaxonomyStore();

  const [searchQuery, setSearchQuery] = useState('');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState<string | null>(null);

  const filteredCategories = categories.filter((category) => {
    const query = searchQuery.toLowerCase();
    return (
      category.name.toLowerCase().includes(query) ||
      category.description?.toLowerCase().includes(query)
    );
  });

  const handleDelete = async () => {
    if (!categoryToDelete) return;

    try {
      await deleteCategory(categoryToDelete);
      setDeleteModalOpen(false);
      setCategoryToDelete(null);
    } catch (error) {
      console.error('Failed to delete category:', error);
      alert('Failed to delete category. It may have associated groups.');
    }
  };

  const openDeleteModal = (id: string) => {
    setCategoryToDelete(id);
    setDeleteModalOpen(true);
  };

  const columns = [
    {
      key: 'icon',
      label: 'Icon',
      render: (category: Category) => <CategoryIconCell category={category} />,
    },
    {
      key: 'name',
      label: 'Name',
      render: (category: Category) => (
        <div>
          <div className="font-medium text-foreground">{category.name}</div>
          {category.description && (
            <div className="text-xs text-muted-foreground mt-1">{category.description}</div>
          )}
        </div>
      ),
    },
    {
      key: 'groups',
      label: 'Groups',
      render: (category: Category) => {
        const categoryGroups = groups.filter((g) => g.categoryId === category.id);
        return (
          <span className="text-muted-foreground">
            {categoryGroups.length} group{categoryGroups.length !== 1 ? 's' : ''}
          </span>
        );
      },
    },
  ];

  return (
    <div className="mx-auto max-w-7xl p-4 sm:p-6 lg:p-8">
      {/* Header */}
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground sm:text-3xl">Categories</h1>
          <p className="text-muted-foreground mt-1">Manage UTM zone categories and their settings</p>
        </div>
        <button
          onClick={() => navigate('/admin/uzamaps/categories/new')}
          className="flex w-full items-center justify-center space-x-2 rounded-lg bg-primary px-4 py-2 text-primary-foreground transition-colors hover:bg-primary/90 sm:w-auto"
        >
          <Plus size={18} />
          <span>Add Category</span>
        </button>
      </div>

      {/* Data Table */}
      {isLoading ? (
        <div className="text-center py-12">
          <div className="text-muted-foreground">Loading categories...</div>
        </div>
      ) : (
        <UtmDataTable
          data={filteredCategories}
          columns={columns}
          searchPlaceholder="Search categories..."
          onSearch={setSearchQuery}
          itemsPerPage={10}
          mobileCardRender={(category) => {
            const categoryGroups = groups.filter((g) => g.categoryId === category.id);
            return (
              <div className="space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <CategoryIconCell category={category} />
                    <div>
                      <div className="font-semibold text-foreground">{category.name}</div>
                      {category.description && (
                        <div className="mt-1 text-xs text-muted-foreground">{category.description}</div>
                      )}
                    </div>
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {categoryGroups.length} group{categoryGroups.length !== 1 ? 's' : ''}
                  </span>
                </div>
                <div className="flex items-center justify-end gap-2">
                  <button
                    onClick={() => navigate(`/admin/uzamaps/categories/${category.id}/edit`)}
                    className="rounded p-2 text-primary transition-colors hover:bg-accent hover:text-primary/80"
                    title="Edit"
                  >
                    <Edit size={16} />
                  </button>
                  <button
                    onClick={() => openDeleteModal(category.id)}
                    className="rounded p-2 text-destructive transition-colors hover:bg-accent hover:text-destructive/80"
                    title="Delete"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            );
          }}
          actions={(category) => (
            <>
              <button
                onClick={() =>
                  navigate(`/admin/uzamaps/categories/${category.id}/edit`)
                }
                className="p-2 text-primary hover:text-primary/80 hover:bg-accent rounded transition-colors"
                title="Edit"
              >
                <Edit size={16} />
              </button>
              <button
                onClick={() => openDeleteModal(category.id)}
                className="p-2 text-destructive hover:text-destructive/80 hover:bg-accent rounded transition-colors"
                title="Delete"
              >
                <Trash2 size={16} />
              </button>
            </>
          )}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deleteModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card border border-border rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold text-foreground mb-2">Delete Category</h3>
            <p className="text-muted-foreground mb-6">
              Are you sure you want to delete this category? This action cannot be undone. All
              associated groups will also be deleted.
            </p>
            <div className="flex items-center justify-end space-x-3">
              <button
                onClick={() => {
                  setDeleteModalOpen(false);
                  setCategoryToDelete(null);
                }}
                className="px-4 py-2 bg-muted hover:bg-accent text-foreground rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-destructive hover:bg-destructive/90 text-destructive-foreground rounded-lg transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UtmCategoryList;
